import { PageableRequest, PageableResponseMetadata } from './Pageable';
import { SortOrder } from './Sorting';

export type Surgeon = {
  id: string;
  licenseNumber?: string;
  name: string;
  nameFormatted: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  lockedStatus?: boolean;
  hospitalName?: string;
  specialty?: SurgeonSpecialties[];
  emailAddresses?: string[];
  contactNumbers?: string[];
  primaryEmail?: string;
  secondaryEmail?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
  note?: string;
  status?: boolean;
  belongsToHospital?: boolean;
};

export const makeSurgeon = (data: Partial<Surgeon>): Surgeon => ({
  id: '',
  name: '',
  nameFormatted: '',
  status: true,
  ...data,
});

export type SurgeonsResponse = {
  metadata: PageableResponseMetadata;
  surgeons: Surgeon[];
};

export enum SurgeonsSortField {
  surgeonName = 'surgeon_name',
  Specialty = 'specialty',
  Status = 'status',
}

export type SurgeonsFilters = Partial<{
  search: string;
  sort_order: SortOrder;
  sort_by: SurgeonsSortField;
  active?: boolean;
  verified?: boolean;
  belongs_to_hospital?: boolean;
}>;

export type SurgeonsRequestFilters = Partial<PageableRequest & SurgeonsFilters>;

export enum SurgeonSpecialties {
  orthopedics = 'Orthopedics',
  spine = 'Spine',
  trauma = 'Trauma',
  earNoseThroat = 'Ear Nose Throat',
  plasticAndReconstructive = 'Plastic & Reconstructive',
  sportsMedicine = 'Sports Medicine',
  generalSurgery = 'General Surgery',
  oncology = 'Oncology',
  hip = 'Hip',
  knee = 'Knee',
  shoulder = 'Shoulder',
  elbow = 'Elbow',
  hand = 'Hand',
  footAndAnkle = 'Foot and Ankle',
  pediatricOrthopedics = 'Pediatric Orthopedics',
}

type SurgeonEditableData = Omit<Surgeon, 'id' | 'name' | 'nameFormatted'>;

export type UpdateSurgeonPayload = SurgeonEditableData & {
  surgeonName?: string;
  specialty?: SurgeonSpecialties[];
};

export type MergeSurgeonsPayload = {
  oldSurgeonIds: string[];
  newSurgeonId: string;
  dryRun?: boolean;
};

export type CreateSurgeonsPayload = {
  surgeons: (Partial<UpdateSurgeonPayload> & {
    id: string;
    name?: string;
    specialty?: string[];
  })[];
};

/** a surgeon flagged by SmartWays/ETL surgeon matching, with its best match */
export type UnrecognisedSurgeon = {
  surgeonId: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  hospitalLocationId: string;
  hospitalName: string;
  /** first name missing or only an initial, e.g. "J Smith" */
  ambiguousName: boolean;
  outcome: 'matched' | 'below_threshold' | 'ambiguous' | null;
  dryRun: boolean | null;
  candidateSurgeonId: string | null;
  candidateName: string | null;
  score: number | null;
  matchedAt: string | null;
};

export type UnrecognisedSurgeonMergeResponse = {
  aliasesTransferred: number;
  surgeriesTransferred: number;
  /** false when the entered name wasn't saved as an alias (no supplier orders, or already an alias elsewhere) */
  aliasSaved: boolean;
};
